import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export interface PublicJob {
  id: number;
  title: string;
  department: string;
  description: string;
  requirements?: string;
  location: string;
  employment_type: string;
  positions_count: number;
  salary_min?: number;
  salary_max?: number;
  created_at: string;
}

export interface JobFilters {
  location?: string;
  employment_type?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApplicationData {
  applicant_name: string;
  applicant_email: string;
  applicant_phone?: string;
  cover_letter?: string;
  resume?: File;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { message: string };
}

interface JobsResponse {
  success: boolean;
  jobs: PublicJob[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

interface SingleJobResponse {
  success: boolean;
  job: PublicJob;
}

// Create axios instance for public APIs (no auth token)
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Get all public job openings
 */
export const getPublicJobs = async (filters?: JobFilters): Promise<JobsResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters?.location) params.append('location', filters.location);
    if (filters?.employment_type) params.append('employment_type', filters.employment_type);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response = await publicApi.get(`/public/jobs?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      jobs: [],
      pagination: { currentPage: 1, pageSize: 20, totalItems: 0, totalPages: 0 }
    };
  }
};

/**
 * Get single job details
 */
export const getPublicJobById = async (id: number | string): Promise<ApiResponse<PublicJob>> => {
  try {
    const response = await publicApi.get<SingleJobResponse>(`/public/jobs/${id}`);
    return {
      success: true,
      data: response.data.job
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Job not found' }
    };
  }
};

/**
 * Submit job application
 */
export const submitApplication = async (
  jobId: number | string,
  data: ApplicationData
): Promise<ApiResponse<{ id: number; job_title: string }>> => {
  try {
    const formData = new FormData();
    formData.append('applicant_name', data.applicant_name);
    formData.append('applicant_email', data.applicant_email);
    if (data.applicant_phone) formData.append('applicant_phone', data.applicant_phone);
    if (data.cover_letter) formData.append('cover_letter', data.cover_letter);
    if (data.resume) formData.append('resume', data.resume);

    const response = await publicApi.post(`/public/jobs/${jobId}/apply`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return {
      success: true,
      data: response.data.data
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to submit application' }
    };
  }
};

// Location options for the job form dropdown
export const locationOptions = [
  { value: 'Remote', label: 'Remote' },
  { value: 'Hybrid - Bengaluru', label: 'Hybrid - Bengaluru' },
  { value: 'Hybrid - Mumbai', label: 'Hybrid - Mumbai' },
  { value: 'Hybrid - Delhi', label: 'Hybrid - Delhi' },
  { value: 'On-site - Bengaluru', label: 'On-site - Bengaluru' },
  { value: 'On-site - Mumbai', label: 'On-site - Mumbai' },
  { value: 'On-site - Delhi', label: 'On-site - Delhi' }
];

// Employment type options
export const employmentTypeOptions = [
  { value: 'full-time', label: 'Full Time' },
  { value: 'part-time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Internship' }
];

// Currency configuration - All salary amounts are in INR (Indian Rupees)
export const CURRENCY = {
  symbol: '₹',
  code: 'INR',
  locale: 'en-IN'
};

// Helper function to format salary in INR
export const formatSalaryINR = (min?: number, max?: number): string | null => {
  if (!min && !max) return null;
  const format = (n: number) => n.toLocaleString('en-IN');
  if (min && max) return `₹${format(min)} - ₹${format(max)}`;
  if (min) return `From ₹${format(min)}`;
  return `Up to ₹${format(max!)}`;
};
